/**
 * ☆ Saved-tenders API client — the user's personal tender shortlist. All routes
 * are cookie-authenticated under /me/saved-tenders (login-required). Distinct
 * from saved *searches* (saved queries + alerts): this saves specific tenders.
 */
import { apiRequest, apiRaw } from './api';
import { rowToItem, type CatalogItem, type CatalogRow } from './catalog-client';

/** The ids of the tenders the current user has saved — drives the ☆ state on cards. */
export async function fetchSavedTenderIds(): Promise<string[]> {
  const res = await apiRequest<{ ids: string[] }>('/me/saved-tenders/ids');
  return res.ids ?? [];
}

/**
 * The user's saved tenders as full catalog cards, newest-saved first.
 *
 * `lang` is NOT optional in practice: the API falls back to the Uzbek columns
 * whenever it is missing (`CatalogService.pickTitle`/`pickLabel`), so omitting
 * it renders a Russian UI full of Uzbek titles, regions and categories.
 * Callers must pass the active locale, and cache it in their query key.
 */
export async function listSavedTenders(lang?: string): Promise<CatalogItem[]> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  const res = await apiRaw<{ data: CatalogRow[] }>(`/me/saved-tenders${qs}`);
  return (res.data ?? []).map(rowToItem);
}

/** Save a tender (idempotent). */
export async function saveTender(tenderId: string): Promise<void> {
  await apiRequest(`/me/saved-tenders/${encodeURIComponent(tenderId)}`, { method: 'POST' });
}

/** Un-save a tender (idempotent). */
export async function unsaveTender(tenderId: string): Promise<void> {
  await apiRequest(`/me/saved-tenders/${encodeURIComponent(tenderId)}`, { method: 'DELETE' });
}
